"use client";

import TrashIcon from "@/app/assets/icon-trash.svg";
import InfoIcon from "@/app/assets/info-icon.svg";
import SwitchIcon from "@/app/assets/switch-icon.svg";
import {
  type Location,
  getPackageAggregates,
  isPackageFilled,
  itemWeightToKg,
} from "@/app/encomendas/types";
import { useEncomendaData } from "@/app/encomendas/useEncomendaData";
import { usePackageHandlers } from "@/app/encomendas/usePackageHandlers";
import ChipButtonWide from "@/components/Atoms/ChipButtonWide";
import LoadingLogo from "@/components/Atoms/LoadingLogo";
import { useDemoTheme } from "@/context/DemoThemeContext";
import ConfirmDeleteModal from "@/components/Atoms/Modal/ConfirmDelete";
import PresetSelect from "@/components/Atoms/PresetSelect";
import RouteSearchInput, {
  type RouteSearchInputHandle,
} from "@/components/Atoms/RouteSearchInput";
import EncomendasSteps from "@/components/Atoms/Steps/EncomendasSteps";
import {
  FieldLabel,
  SectionHeader,
  SubHeader,
  Unit,
} from "@/components/Atoms/Typography";
import SwapArrowsIcon from "@/components/Icons/SwapArrowsIcon";
import CotacaoDialog, {
  fetchQuickQuote,
  fetchQuotes,
} from "@/components/Molecules/CotacaoDialog";
import PackageInfoModal from "@/components/Molecules/PackageInfoModal";
import type { RouteMapHandle } from "@/components/Molecules/RouteMap";
import {
  readBasePackages,
  writeBasePackages,
  type StoredPackageLike,
} from "@/lib/encomendas/packageStorage";
import { primaryColor } from "@/lib/theme";
import { useEmbarcaLogValidation } from "@/utils/EmbarcaLogEnabledOperators";
import { formatLocalityName } from "@/utils/formatLocality";
import useShippingOptionStore from "@/store/useShippingOptionStore";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  ChevronDown,
  Loader2,
  Map,
  MapPin,
  Package,
  Pencil,
  Truck,
} from "lucide-react";
import dynamic from "next/dynamic";
import Image from "next/image";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

/** Nome de cada dimensão dentro da frase "Informe a largura e a altura". */
const DIM_LABELS = {
  lengthCm: "o comprimento",
  widthCm: "a largura",
  heightCm: "a altura",
} as const;

/** "a, b e c" — lista em português, sem vírgula antes do "e". */
const joinPtBR = (parts: string[]) =>
  parts.length <= 1
    ? parts.join("")
    : `${parts.slice(0, -1).join(", ")} e ${parts[parts.length - 1]}`;

/** O que falta em um lugar do formulário: a rota ou um pacote específico. */
interface BlockGroup {
  id: string;
  /** Onde corrigir — "Rota" ou o nome do pacote. */
  location: string;
  issues: string[];
}

const BoxPreview3D = dynamic<{
  length: string;
  width: string;
  height: string;
}>(() => import("@/components/Molecules/BoxPreview3D"), { ssr: false });
const RouteMap = dynamic(() => import("@/components/Molecules/RouteMap"), {
  ssr: false,
}) as React.ForwardRefExoticComponent<
  {
    originCity: string;
    originState: string;
    originIbge?: number;
    destCity: string;
    destState: string;
    destIbge?: number;
  } & React.RefAttributes<RouteMapHandle>
>;

// Medir o DOM tem que acontecer antes da pintura, senão o primeiro quadro sai
// com a posição inicial e o elemento "pula" para o lugar certo. No servidor não
// há DOM e `useLayoutEffect` avisa no console, então lá ele vira `useEffect`.
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

export default function EncomendasPage() {
  const hasAccess = useEmbarcaLogValidation();
  const setShippingOption = useShippingOptionStore((s) => s.setShippingOption);

  const data = useEncomendaData();
  const {
    awaitingRouteParams,
    origin,
    setOrigin,
    destination,
    setDestination,
    selectedOrigin,
    setSelectedOrigin,
    selectedDestination,
    setSelectedDestination,
    routeOriginText,
    setRouteOriginText,
    routeDestText,
    setRouteDestText,
    routeOrigin,
    setRouteOrigin,
    routeDest,
    setRouteDest,
    allRoutes,
    routeLoading,
    packages,
    setPackages,
    packagePresets,
    inputValues,
    setInputValues,
  } = data;

  const handlers = usePackageHandlers({
    packages,
    setPackages,
    packagePresets,
    inputValues,
    setInputValues,
    selectedOrigin,
    selectedDestination,
    origin,
    destination,
  });
  const {
    cotacaoOpen,
    setCotacaoOpen,
    cotacaoLoading,
    cotacaoError,
    setCotacaoError,
    cotacaoOptions,
    setCotacaoOptions,
    cotacaoPackages,
    getInputValue,
    getInputError,
    getItemInputValue,
    getItemInputError,
    handlePackageFieldChange,
    handlePresetChange,
    handleItemFieldChange,
    addPackage,
    isFormReady,
    isDirty,
    markQuoteStale,
    quoteRetryExhausted,
    retryQuote,
  } = handlers;

  const [originError, setOriginError] = useState("");
  const [destinationError, setDestinationError] = useState("");
  const [showErrors, setShowErrors] = useState(false);
  const [swapping, setSwapping] = useState(false);
  const [swapRotated, setSwapRotated] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { themeReady } = useDemoTheme();
  const routeMapRef = useRef<RouteMapHandle>(null);
  const destInputRef = useRef<RouteSearchInputHandle>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [packageInfoModalOpen, setPackageInfoModalOpen] = useState(false);
  const [valorDisplay, setValorDisplay] = useState<Record<string, string>>({});
  const prevPkgCount = useRef(packages.length);
  const [newPkgId, setNewPkgId] = useState<string | null>(null);
  const nameRefs = useRef<Record<string, HTMLSpanElement | null>>({});

  const selectAllText = (el: HTMLElement) => {
    const range = document.createRange();
    range.selectNodeContents(el);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  };

  // Enquanto essa condição for falsa a página renderiza só o loader, então
  // nenhum ref do layout existe ainda. Qualquer efeito que meça o DOM precisa
  // depender dela para remedir quando o conteúdo real entra.
  const layoutReady = mounted && themeReady && !awaitingRouteParams;

  // Com mais de um pacote o card de cotação se solta e flutua no canto.
  const quotePanelFloating = packages.length > 1;
  const contentRef = useRef<HTMLDivElement>(null);
  const quoteColumnRef = useRef<HTMLDivElement>(null);
  const panelsRowRef = useRef<HTMLDivElement>(null);
  // O card flutuante entra depois da saída do encaixado (`AnimatePresence` com
  // `mode="wait"`), então um ref comum ainda estaria vazio quando o efeito
  // rodasse. Guardar o elemento em estado faz o efeito remedir na hora em que
  // ele de fato monta.
  const [quoteCardEl, setQuoteCardEl] = useState<HTMLDivElement | null>(null);
  const [quotePanelInset, setQuotePanelInset] = useState({
    left: 0,
    bottom: 24,
  });

  // O card é `fixed`, então sozinho ele encostaria na borda da janela e
  // passaria por cima do resto da página. `left` vem da própria coluna do
  // painel, para o card flutuante ocupar exatamente a mesma faixa horizontal
  // (e a mesma distância dos pacotes) que a versão encaixada. `bottom` é
  // preso entre dois limites, calculados sempre como distância até a base da
  // janela porque é por `bottom` que o card é posicionado.
  useIsomorphicLayoutEffect(() => {
    if (!quotePanelFloating || !layoutReady || !quoteCardEl) return;
    const content = contentRef.current;
    const column = quoteColumnRef.current;
    const row = panelsRowRef.current;
    if (!content || !column || !row) return;
    const FLOAT_GAP = 24;
    const update = () => {
      const left = column.getBoundingClientRect().left;
      // Piso: enquanto o fim do conteúdo estiver longe, o card fica a
      // `FLOAT_GAP` da base; quando o fim sobe, ele para na mesma linha em que
      // a coluna da esquerda acaba, sem cobrir o rodapé.
      const restingBottom = Math.max(
        FLOAT_GAP,
        window.innerHeight - content.getBoundingClientRect().bottom
      );
      // Teto: o topo do card nunca passa do topo da linha de painéis — a mesma
      // linha onde a seção de pacotes começa, então o respiro em relação ao
      // card de origem e destino é o mesmo que os pacotes têm. `offsetHeight`
      // é a altura de layout, imune ao `scale` da animação de entrada.
      const highestBottom =
        window.innerHeight -
        row.getBoundingClientRect().top -
        quoteCardEl.offsetHeight;
      const bottom = Math.min(restingBottom, highestBottom);
      setQuotePanelInset((prev) =>
        prev.left === left && prev.bottom === bottom ? prev : { left, bottom }
      );
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update, { passive: true });
    // A altura do card muda quando a cotação carrega, e o fim do conteúdo muda
    // quando um pacote entra ou sai. Sem observar, o teto continuaria calculado
    // sobre medidas velhas até o próximo scroll.
    const observer = new ResizeObserver(update);
    observer.observe(quoteCardEl);
    observer.observe(content);
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [quotePanelFloating, layoutReady, quoteCardEl]);

  // O texto é editado direto no DOM (contentEditable), então o React não
  // reconcilia o que o usuário digitou. Escrever o valor final de volta no
  // elemento mantém tela e estado iguais mesmo quando o nome cai no padrão.
  const commitPackageName = (
    pkgId: string,
    el: HTMLElement,
    fallback: string
  ) => {
    const typed = (el.textContent ?? "")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 40);
    // Texto igual ao padrão não conta como renomeação: focar o título e sair
    // sem digitar não pode gravar "Pacote 01" como nome — esse nome vazaria
    // para a descrição do item na etapa seguinte.
    const name = typed && typed !== fallback ? typed : undefined;
    el.textContent = name ?? fallback;
    setPackages((prev) =>
      prev.map((p) => (p.id === pkgId ? { ...p, name } : p))
    );
    // O snapshot do localStorage só é reescrito quando uma cotação roda, e
    // renomear não altera preço (logo não dispara cotação). Sem isso o nome
    // nunca chegaria às etapas seguintes.
    const stored = readBasePackages<StoredPackageLike>();
    if (stored?.some((p) => p.id === pkgId)) {
      writeBasePackages(
        stored.map((p) => (p.id === pkgId ? { ...p, name } : p))
      );
    }
  };

  // Price animation state
  const prevPriceRef = useRef<number | null>(null);
  const [priceDelta, setPriceDelta] = useState<{
    value: number;
    key: number;
  } | null>(null);
  const [priceAnimDir, setPriceAnimDir] = useState<1 | -1>(1);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    setMounted(true);
  }, []);

  useEffect(() => {
    if (packages.length > prevPkgCount.current) {
      const newPkg = packages[packages.length - 1];
      setNewPkgId(newPkg.id);
      setTimeout(() => setNewPkgId(null), 400);
    }
    prevPkgCount.current = packages.length;
  }, [packages.length]);

  // Detect price changes and fire the floating delta + number scroll animation
  useEffect(() => {
    if (cotacaoLoading || cotacaoOptions.length === 0) return;
    const newPrice = cotacaoOptions[0]?.priceBRL ?? null;
    if (newPrice === null) return;
    if (prevPriceRef.current !== null && newPrice !== prevPriceRef.current) {
      const delta = newPrice - prevPriceRef.current;
      setPriceAnimDir(delta > 0 ? 1 : -1);
      setPriceDelta({ value: delta, key: Date.now() });
    }
    prevPriceRef.current = newPrice;
  }, [cotacaoOptions, cotacaoLoading]);

  if (!hasAccess) return null;

  // O que impede a cotação, agrupado por onde se resolve. Antes era uma lista
  // de frases genéricas ("as dimensões de todos os pacotes") repetidas uma vez
  // por pacote — com dois pacotes incompletos, a mesma frase entrava duas
  // vezes. Agora cada grupo aponta o pacote e diz quais campos faltam nele.
  const cotacaoBlockGroups: BlockGroup[] = [];

  const routeIssues: string[] = [];
  if (!selectedOrigin) routeIssues.push("Selecione a cidade de envio");
  if (!selectedDestination) routeIssues.push("Selecione a cidade de retirada");
  if (routeIssues.length > 0)
    cotacaoBlockGroups.push({ id: "rota", location: "Rota", issues: routeIssues });

  packages.forEach((pkg, index) => {
    const issues: string[] = [];

    const missingDims = (["lengthCm", "widthCm", "heightCm"] as const).filter(
      (field) => {
        const v = parseFloat(
          (inputValues[`${pkg.id}_${field}`] || "").replace(",", ".")
        );
        return !v || v <= 0;
      }
    );
    if (missingDims.length > 0)
      issues.push(
        `Informe ${joinPtBR(missingDims.map((field) => DIM_LABELS[field]))}`
      );

    // Sem `break`: o usuário vê de uma vez tudo o que falta no pacote, em vez
    // de descobrir um campo por vez a cada correção. O item só é nomeado se o
    // usuário deu um nome a ele — "de item 2" não diz nada que o grupo do
    // pacote já não diga, então nesse caso a frase fica só com o campo.
    (pkg.items || []).forEach((item) => {
      const itemName = item.name?.trim();
      const suffix = itemName ? ` de ${itemName}` : "";
      if (itemWeightToKg(item) <= 0) issues.push(`Informe o peso${suffix}`);
      const valRaw =
        inputValues[`${pkg.id}_item_${item.id}_unitValueBRL`] ??
        (item.unitValueBRL ? String(item.unitValueBRL) : "");
      if (!valRaw || parseFloat(valRaw) <= 0)
        issues.push(`Informe o valor declarado${suffix}`);
    });

    if (getPackageAggregates(pkg).weightKg > 30)
      issues.push("O peso somado passa de 30 kg");

    // Itens sem nome geram frases idênticas ("Informe o peso"): sem o Set, a
    // mesma linha apareceria uma vez por item — e com a mesma key no React.
    if (issues.length > 0)
      cotacaoBlockGroups.push({
        id: pkg.id,
        location: pkg.name || `Pacote ${String(index + 1).padStart(2, "0")}`,
        issues: Array.from(new Set(issues)),
      });
  });

  // O tooltip continua sendo uma lista rasa — com o local na frente, para que
  // duas pendências iguais em pacotes diferentes não virem linhas idênticas.
  const cotacaoBlockReasons = cotacaoBlockGroups.flatMap((group) =>
    group.issues.map((issue) => `${group.location}: ${issue}`)
  );
  const isCotacaoBlocked = cotacaoBlockGroups.length > 0;

  // A cotação automática só roda com o formulário válido. Sem repetir essa
  // condição aqui, limpar um campo deixaria `isDirty` preso em true e o botão
  // giraria em "Carregando cotação" para sempre — agora ele só desabilita.
  const isQuoting =
    isFormReady && !isCotacaoBlocked && (cotacaoLoading || isDirty);

  if (!layoutReady) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-paper">
        <div className="flex flex-col items-center gap-6">
          <LoadingLogo />
          <div className="h-px w-full bg-line" />
          <div className="flex gap-1.5 items-end">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="w-2 h-2 rounded-full animate-bounce"
                style={{
                  backgroundColor: primaryColor,
                  animationDelay: `${i * 0.15}s`,
                }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12 pt-4 md:pt-6">
      <ConfirmDeleteModal
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        onConfirm={() => {
          if (!pendingDeleteId) {
            setConfirmDeleteOpen(false);
            return;
          }
          markQuoteStale();
          setPackages((prev) => prev.filter((p) => p.id !== pendingDeleteId));
          setPendingDeleteId(null);
          setConfirmDeleteOpen(false);
        }}
        title="Remover pacote"
        message="Tem certeza que deseja remover este pacote? Os dados preenchidos serão perdidos."
        confirmText="Remover"
      />

      <PackageInfoModal
        open={packageInfoModalOpen}
        onClose={() => setPackageInfoModalOpen(false)}
      />

      <div ref={contentRef} className="max-w-[1381px] mx-auto px-4 md:px-6">
        <div className="md:hidden">
          <EncomendasSteps hideUserOptions={true} />
        </div>
        <div className="hidden md:block">
          <EncomendasSteps />
        </div>

        <div className="space-y-5">
          {/* Origin & Destination card */}
          <div className="card">
            <SectionHeader
              title="Origem e destino"
              icon={
                <MapPin className="w-5 h-5" style={{ color: primaryColor }} />
              }
            />

            <div className="px-6 pb-6 pt-4 flex flex-col md:flex-row gap-6 md:items-stretch">
              {/* Inputs column */}
              <div className="flex-1 min-w-0 flex gap-4 items-start">
                {/* Connector column mirrors the right column's structure exactly:
                    two stacked rows (invisible label spacer + h-14 box) with the
                    same gap-3, so each pin lines up with its input automatically.
                    The dashed line + swap button is one overlay centered on the column. */}
                <div className="relative w-7 shrink-0 hidden md:flex flex-col gap-6">
                  {/* Dashed connector + swap button, spanning from the first pin to the last */}
                  <div className="absolute inset-0 flex flex-col items-center pointer-events-none pt-[60px] pb-10">
                    <div
                      className="flex-1 w-px border-l-2 border-dashed"
                      style={{ borderColor: primaryColor }}
                    />
                    <button
                      id="btn-encomendas-swap-route"
                      type="button"
                      onClick={() => {
                        if (swapping) return;
                        setSwapping(true);
                        setTimeout(() => {
                          const prevOrigin = routeOrigin;
                          const prevDest = routeDest;
                          const prevSelOrigin = selectedOrigin;
                          const prevSelDest = selectedDestination;
                          const prevOriginName = origin;
                          const prevDestName = destination;
                          setRouteOrigin(prevDest);
                          setRouteDest(prevOrigin);
                          setRouteOriginText("");
                          setRouteDestText("");
                          setSelectedOrigin(prevSelDest);
                          setSelectedDestination(prevSelOrigin);
                          setOrigin(prevDestName);
                          setDestination(prevOriginName);
                          // Animate the map without remounting
                          routeMapRef.current?.swap(
                            prevDest?.name ?? "",
                            prevDest?.state ?? "",
                            prevOrigin?.name ?? "",
                            prevOrigin?.state ?? ""
                          );
                          setSwapRotated((r) => !r);
                          setTimeout(() => setSwapping(false), 200);
                        }, 150);
                      }}
                      className="pointer-events-auto my-1 w-7 h-7 rounded-full bg-paper border flex items-center justify-center hover:opacity-80 transition-opacity"
                      style={{ borderColor: primaryColor, color: primaryColor }}
                      title="Inverter rota"
                    >
                      <SwapArrowsIcon
                        size={12}
                        style={{
                          transition:
                            "transform 0.35s cubic-bezier(0.4,0,0.2,1)",
                          transform: swapRotated
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                        }}
                      />
                    </button>
                    <div
                      className="flex-1 w-px border-l-2 border-dashed"
                      style={{ borderColor: primaryColor }}
                    />
                  </div>
                  {/* Row 1 — aligns with the Envio input */}
                  <div className="flex flex-col">
                    <span
                      className="invisible mb-1.5 select-none text-fine"
                      aria-hidden
                    >
                      ·
                    </span>
                    <div className="h-14 flex items-center justify-center">
                      <Package
                        size={18}
                        strokeWidth={1.75}
                        color={primaryColor}
                        className="shrink-0"
                      />
                    </div>
                  </div>
                  {/* Row 2 — aligns with the Retirada input */}
                  <div className="flex flex-col">
                    <span
                      className="invisible mb-1.5 select-none text-fine"
                      aria-hidden
                    >
                      ·
                    </span>
                    <div className="h-14 flex items-center justify-center">
                      <ChevronDown
                        size={18}
                        strokeWidth={1.75}
                        color={primaryColor}
                        className="shrink-0"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex-1 min-w-0 flex flex-col gap-6">
                  <div>
                    <FieldLabel
                      className={`mb-1.5 ${showErrors && originError ? "!text-danger" : ""}`}
                    >
                      Envio
                    </FieldLabel>
                    <RouteSearchInput
                      idScope="origin"
                      placeholder="ex. Curitiba, PR"
                      value={routeOriginText}
                      selectedLocation={routeOrigin}
                      onChange={setRouteOriginText}
                      onSelect={(loc) => {
                        setRouteOrigin(loc);
                        setRouteOriginText("");
                        const sel: Location = {
                          id: loc.id,
                          name: loc.point_name ?? loc.name,
                          state: loc.state,
                          slug: loc.slug,
                          ibge_id: loc.ibge_id,
                          city_name: loc.name,
                          state_uf: loc.state,
                        };
                        setSelectedOrigin(sel);
                        setOrigin(formatLocalityName(loc.point_name, loc.name));
                        if (routeDest?.id === loc.id) {
                          setRouteDest(null);
                          setRouteDestText("");
                          setSelectedDestination(null);
                          setDestination("");
                        }
                        if (showErrors) setOriginError("");
                      }}
                      onClear={() => {
                        setRouteOrigin(null);
                        setRouteOriginText("");
                        setSelectedOrigin(null);
                        setOrigin("");
                      }}
                      excludeId={routeDest?.id ?? null}
                      inputStyles={
                        showErrors && originError ? "!border-danger" : ""
                      }
                      loading={routeLoading}
                      allRoutes={allRoutes}
                      fadeValue={swapping}
                      onAfterSelect={() => {
                        if (!routeDest) destInputRef.current?.focus();
                      }}
                    />
                    {showErrors && originError && (
                      <p className="mt-1 text-fine text-danger">
                        {originError}
                      </p>
                    )}
                  </div>
                  <div>
                    <FieldLabel
                      className={`mb-1.5 ${showErrors && destinationError ? "!text-danger" : ""}`}
                    >
                      Retirada
                    </FieldLabel>
                    <RouteSearchInput
                      idScope="destination"
                      ref={destInputRef}
                      placeholder="ex. São Paulo, SP"
                      value={routeDestText}
                      selectedLocation={routeDest}
                      onChange={setRouteDestText}
                      onSelect={(loc) => {
                        setRouteDest(loc);
                        setRouteDestText("");
                        const sel: Location = {
                          id: loc.id,
                          name: loc.point_name ?? loc.name,
                          state: loc.state,
                          slug: loc.slug,
                          ibge_id: loc.ibge_id,
                          city_name: loc.name,
                          state_uf: loc.state,
                        };
                        setSelectedDestination(sel);
                        setDestination(
                          formatLocalityName(loc.point_name, loc.name)
                        );
                        if (showErrors) setDestinationError("");
                      }}
                      onClear={() => {
                        setRouteDest(null);
                        setRouteDestText("");
                        setSelectedDestination(null);
                        setDestination("");
                      }}
                      excludeId={routeOrigin?.id ?? null}
                      inputStyles={
                        showErrors && destinationError ? "!border-danger" : ""
                      }
                      loading={routeLoading}
                      allRoutes={allRoutes}
                      fadeValue={swapping}
                    />
                    {showErrors && destinationError && (
                      <p className="mt-1 text-fine text-danger">
                        {destinationError}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Map or placeholder */}
              <div className="hidden md:block w-px bg-line shrink-0" />
              {routeOrigin && routeDest ? (
                <div className="w-full z-0 md:flex-1 rounded-control overflow-hidden border border-line h-[280px] md:h-[260px] relative pointer-events-none md:pointer-events-auto">
                  <RouteMap
                    ref={routeMapRef}
                    originCity={routeOrigin.name}
                    originState={routeOrigin.state}
                    originIbge={routeOrigin.ibge_id}
                    destCity={routeDest.name}
                    destState={routeDest.state}
                    destIbge={routeDest.ibge_id}
                  />
                </div>
              ) : (
                <div className="w-full md:flex-1 rounded-control border border-dashed border-line flex flex-col items-center justify-center gap-2 h-[280px] md:h-[260px]">
                  <Map className="w-8 h-8 text-mist" strokeWidth={1.5} />
                  <p className="text-micro text-mist text-center px-4">
                    Selecione origem e destino para ver a rota
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Packages + Quote side-by-side */}
          <div
            ref={panelsRowRef}
            className="flex flex-col lg:flex-row gap-4 items-start"
          >
            {/* Package cards */}
            <section className="w-full flex-1 min-w-0 space-y-4 overflow-hidden">
              {packages.map((pkg, index) => {
                const defaultName = `Pacote ${String(index + 1).padStart(2, "0")}`;
                return (
                  <div
                    key={pkg.id}
                    className={`card group${pkg.id === newPkgId ? " animate-pkg-enter" : ""}`}
                  >
                    <div
                      className="w-full flex items-center justify-between px-6 py-4 cursor-text transition-colors has-[[role=textbox]:focus]:bg-ground/50"
                      onClick={(e) => {
                        const target = e.target as HTMLElement;
                        if (target.closest("button")) return;
                        const el = nameRefs.current[pkg.id];
                        if (!el || el.contains(target)) return;
                        el.focus();
                      }}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0">
                          <Image
                            src={SwitchIcon}
                            alt=""
                            width={16}
                            height={16}
                            className="w-4 h-4"
                          />
                        </div>
                        <span
                          id={`input-encomendas-package-${pkg.id}-name`}
                          ref={(el) => {
                            nameRefs.current[pkg.id] = el;
                          }}
                          contentEditable="plaintext-only"
                          suppressContentEditableWarning
                          role="textbox"
                          aria-label="Nome do pacote"
                          tabIndex={0}
                          spellCheck={false}
                          title="Clique para renomear"
                          onFocus={(e) => selectAllText(e.currentTarget)}
                          onBlur={(e) =>
                            commitPackageName(
                              pkg.id,
                              e.currentTarget,
                              defaultName
                            )
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              e.currentTarget.blur();
                            } else if (e.key === "Escape") {
                              e.currentTarget.textContent =
                                pkg.name || defaultName;
                              e.currentTarget.blur();
                            }
                          }}
                          className="peer -mx-1 min-w-0 max-w-full truncate rounded px-1 text-body font-bold text-ink cursor-text outline-none"
                        >
                          {pkg.name || defaultName}
                        </span>
                        <span
                          aria-hidden
                          className="hidden sm:inline-flex items-center gap-1 shrink-0 text-fine text-slate opacity-0 transition-opacity group-hover:opacity-100 peer-focus:!opacity-0"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                          Renomear
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {packages.length > 1 && (
                          <button
                            id={`btn-encomendas-package-${pkg.id}-remove`}
                            type="button"
                            onClick={() => {
                              if (isPackageFilled(pkg)) {
                                setPendingDeleteId(pkg.id);
                                setConfirmDeleteOpen(true);
                                return;
                              }
                              markQuoteStale();
                              setPackages((p) =>
                                p.filter((x) => x.id !== pkg.id)
                              );
                            }}
                            className="p-2 rounded-full text-mist hover:bg-line transition-colors"
                            aria-label="Remover pacote"
                          >
                            <Image
                              src={TrashIcon}
                              alt="Remover"
                              width={18}
                              height={18}
                            />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="overflow-hidden">
                      <div className="border-t border-line">
                        <div className="px-6 pb-6 pt-5 space-y-6">
                          {packagePresets.length > 0 && (
                            <div className="w-full md:w-[380px]">
                              <FieldLabel className="mb-1.5">
                                Modelos salvos
                              </FieldLabel>
                              <PresetSelect
                                presets={packagePresets}
                                value={pkg.selectedPresetId || ""}
                                onChange={(id) =>
                                  handlePresetChange(pkg.id, id)
                                }
                              />
                            </div>
                          )}

                          {/* 3D preview */}
                          <div className="flex items-center justify-between mb-3">
                            <SubHeader>Dimensões do pacote</SubHeader>
                            {/*
                              Fechado é o círculo de 32px de sempre; no hover a
                              pílula cresce para a esquerda e revela o rótulo,
                              na mesma cor do ícone. O grupo é nomeado porque o
                              card inteiro também é `group` — sem o nome, passar
                              o mouse em qualquer canto do card abriria a
                              pílula. A largura sai de um grid 0fr->1fr em vez
                              de max-width: a animação para exatamente no
                              tamanho do texto, sem o trecho vazio no fim.
                              O nome acessível vem do aria-label, então rótulo e
                              imagem ficam fora da árvore de acessibilidade.
                            */}
                            <button
                              id={`btn-encomendas-package-${pkg.id}-info`}
                              type="button"
                              onClick={() => setPackageInfoModalOpen(true)}
                              aria-label="Informações do pacote"
                              className="group/info flex h-8 shrink-0 items-center justify-end rounded-full pl-0 transition-[padding,opacity] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] hover:pl-3 hover:opacity-90"
                              style={{ backgroundColor: primaryColor }}
                            >
                              <span
                                aria-hidden="true"
                                className="grid grid-cols-[0fr] transition-[grid-template-columns] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover/info:grid-cols-[1fr]"
                              >
                                <span className="overflow-hidden whitespace-nowrap pr-2 text-fine font-semibold text-white opacity-0 transition-opacity duration-200 group-hover/info:opacity-100 group-hover/info:delay-100">
                                  Informações do pacote
                                </span>
                              </span>
                              <span className="flex h-8 w-8 shrink-0 items-center justify-center">
                                <Image
                                  src={InfoIcon}
                                  alt=""
                                  width={16}
                                  height={16}
                                  className="brightness-0 invert"
                                />
                              </span>
                            </button>
                          </div>

                          {/* ── Mobile layout ────────────────────────────────── */}
                          <div className="flex flex-col gap-4 md:hidden">
                            {/* Canvas — centered */}
                            <div className="flex items-center justify-center">
                              <div className="w-full max-w-[280px]">
                                <BoxPreview3D
                                  length={getInputValue(pkg.id, "lengthCm")}
                                  width={getInputValue(pkg.id, "widthCm")}
                                  height={getInputValue(pkg.id, "heightCm")}
                                />
                              </div>
                            </div>

                            {/* Comprimento, Largura, Altura — full width, stacked */}
                            {(
                              [
                                { label: "Comprimento", field: "lengthCm" },
                                { label: "Largura", field: "widthCm" },
                                { label: "Altura", field: "heightCm" },
                              ] as const
                            ).map(({ label, field }) => (
                              <div
                                key={field}
                                className="relative group flex flex-col gap-1"
                              >
                                <FieldLabel
                                  className={
                                    getInputError(pkg.id, field)
                                      ? "!text-danger"
                                      : ""
                                  }
                                >
                                  {label}
                                </FieldLabel>
                                {!!pkg.selectedPresetId && (
                                  <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 whitespace-nowrap rounded-chip bg-ink px-2.5 py-1 text-micro font-semibold text-paper opacity-0 group-hover:opacity-100 transition-opacity z-20">
                                    Modelos salvos não podem ser editados
                                  </span>
                                )}
                                <div
                                  className={`flex items-center h-12 rounded-control border bg-paper overflow-hidden focus-within-secondary transition-colors ${getInputError(pkg.id, field) ? "border-danger" : "border-line"}`}
                                >
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0"
                                    value={getInputValue(pkg.id, field)}
                                    onFocus={(e) => e.target.select()}
                                    onChange={(e) => {
                                      const v = e.target.value.replace(
                                        ",",
                                        "."
                                      );
                                      if (/^\d*\.?\d?$/.test(v))
                                        handlePackageFieldChange(
                                          pkg.id,
                                          field,
                                          v
                                        );
                                    }}
                                    disabled={!!pkg.selectedPresetId}
                                    className="flex-1 h-full px-3 text-body font-semibold text-ink bg-transparent outline-none min-w-0 disabled:cursor-not-allowed disabled:text-slate"
                                  />
                                  <Unit className="flex h-full select-none items-center border-l border-line px-3">
                                    cm
                                  </Unit>
                                </div>
                                {getInputError(pkg.id, field) && (
                                  <p className="text-micro text-danger">
                                    {getInputError(pkg.id, field)}
                                  </p>
                                )}
                              </div>
                            ))}

                            {/* Horizontal separator before Peso / Valor */}
                            <div className="h-px bg-line" />

                            {/* Peso + Valor declarado — below separator */}
                            {(() => {
                              const item = (pkg.items || [])[0];
                              if (!item) return null;
                              const weightRaw =
                                getItemInputValue(
                                  pkg.id,
                                  item.id,
                                  "weightValue"
                                ) ??
                                (item.weightValue != null
                                  ? String(item.weightValue)
                                  : "");
                              const weightError = getItemInputError(
                                pkg.id,
                                item.id,
                                "weightVolume"
                              );
                              const valueError = getItemInputError(
                                pkg.id,
                                item.id,
                                "unitValueBRL"
                              );
                              return (
                                <>
                                  <div className="relative group flex flex-col gap-1">
                                    <FieldLabel
                                      className={
                                        weightError ? "!text-danger" : ""
                                      }
                                    >
                                      Peso
                                    </FieldLabel>
                                    {!!pkg.selectedPresetId && (
                                      <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 whitespace-nowrap rounded-chip bg-ink px-2.5 py-1 text-micro font-semibold text-paper opacity-0 group-hover:opacity-100 transition-opacity z-20">
                                        Modelos salvos não podem ser editados
                                      </span>
                                    )}
                                    <div
                                      className={`flex items-center h-12 rounded-control border bg-paper overflow-hidden focus-within-secondary transition-colors ${weightError ? "border-danger" : "border-line"}`}
                                    >
                                      <input
                                        type="text"
                                        inputMode="decimal"
                                        placeholder="0"
                                        value={weightRaw}
                                        onFocus={(e) => e.target.select()}
                                        onChange={(e) => {
                                          const v = e.target.value.replace(
                                            ",",
                                            "."
                                          );
                                          if (/^\d*\.?\d{0,2}$/.test(v))
                                            handleItemFieldChange(
                                              pkg.id,
                                              item.id,
                                              "weightValue",
                                              v
                                            );
                                        }}
                                        disabled={!!pkg.selectedPresetId}
                                        className="flex-1 h-full px-3 text-body font-semibold text-ink bg-transparent outline-none min-w-0 disabled:cursor-not-allowed disabled:text-slate"
                                      />
                                      <Unit className="flex h-full select-none items-center border-l border-line px-3">
                                        kg
                                      </Unit>
                                    </div>
                                    {weightError && (
                                      <p className="text-micro text-danger">
                                        {weightError}
                                      </p>
                                    )}
                                  </div>
                                  <div className="relative group flex flex-col gap-1">
                                    <FieldLabel
                                      className={
                                        valueError ? "!text-danger" : ""
                                      }
                                    >
                                      Valor declarado
                                    </FieldLabel>
                                    {!!pkg.selectedPresetId && (
                                      <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 whitespace-nowrap rounded-chip bg-ink px-2.5 py-1 text-micro font-semibold text-paper opacity-0 group-hover:opacity-100 transition-opacity z-20">
                                        Modelos salvos não podem ser editados
                                      </span>
                                    )}
                                    <div
                                      className={`flex items-center h-12 rounded-control border bg-paper overflow-hidden focus-within-secondary transition-colors ${valueError ? "border-danger" : "border-line"}`}
                                    >
                                      <Unit className="flex h-full select-none items-center border-r border-line px-3">
                                        R$
                                      </Unit>
                                      <input
                                        type="text"
                                        inputMode="numeric"
                                        placeholder="0,00"
                                        value={
                                          valorDisplay[item.id] ??
                                          (item.unitValueBRL
                                            ? item.unitValueBRL.toLocaleString(
                                                "pt-BR",
                                                {
                                                  minimumFractionDigits: 2,
                                                  maximumFractionDigits: 2,
                                                }
                                              )
                                            : "")
                                        }
                                        onFocus={(e) => e.target.select()}
                                        onChange={(e) => {
                                          const digits = e.target.value.replace(
                                            /\D/g,
                                            ""
                                          );
                                          const cents = parseInt(
                                            digits || "0",
                                            10
                                          );
                                          const display = (
                                            cents / 100
                                          ).toLocaleString("pt-BR", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          });
                                          setValorDisplay((p) => ({
                                            ...p,
                                            [item.id]: display,
                                          }));
                                          handleItemFieldChange(
                                            pkg.id,
                                            item.id,
                                            "unitValueBRL",
                                            String(cents / 100)
                                          );
                                        }}
                                        disabled={!!pkg.selectedPresetId}
                                        className="flex-1 h-full px-3 text-body font-semibold text-ink bg-transparent outline-none min-w-0 disabled:cursor-not-allowed disabled:text-slate"
                                      />
                                    </div>
                                    {valueError && (
                                      <p className="text-micro text-danger">
                                        {valueError}
                                      </p>
                                    )}
                                  </div>
                                </>
                              );
                            })()}
                          </div>

                          {/* ── Desktop layout (unchanged) ───────────────────── */}
                          <div className="hidden md:flex flex-row gap-0 min-w-0">
                            {/* Left — 3D canvas, 40% */}
                            <div className="md:w-[40%] shrink-0 flex items-center justify-center pr-6">
                              <div className="w-full">
                                <BoxPreview3D
                                  length={getInputValue(pkg.id, "lengthCm")}
                                  width={getInputValue(pkg.id, "widthCm")}
                                  height={getInputValue(pkg.id, "heightCm")}
                                />
                              </div>
                            </div>

                            {/* Vertical separator */}
                            <div className="w-px bg-line self-stretch shrink-0" />

                            {/* Right — input grid, 60% */}
                            <div className="md:w-[60%] min-w-0 flex items-center pl-6">
                              <div className="flex gap-6 w-full">
                                {/* Dimensions column */}
                                <div className="flex flex-col gap-3 flex-1 min-w-0">
                                  {(
                                    [
                                      {
                                        label: "Comprimento",
                                        field: "lengthCm",
                                      },
                                      { label: "Largura", field: "widthCm" },
                                      { label: "Altura", field: "heightCm" },
                                    ] as const
                                  ).map(({ label, field }) => (
                                    <div
                                      key={field}
                                      className="relative group flex flex-col gap-1"
                                    >
                                      <FieldLabel
                                        className={
                                          getInputError(pkg.id, field)
                                            ? "!text-danger"
                                            : ""
                                        }
                                      >
                                        {label}
                                      </FieldLabel>
                                      {!!pkg.selectedPresetId && (
                                        <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 whitespace-nowrap rounded-chip bg-ink px-2.5 py-1 text-micro font-semibold text-paper opacity-0 group-hover:opacity-100 transition-opacity z-20">
                                          Modelos salvos não podem ser editados
                                        </span>
                                      )}
                                      <div
                                        className={`flex items-center h-12 rounded-control border bg-paper overflow-hidden focus-within-secondary transition-colors ${getInputError(pkg.id, field) ? "border-danger" : "border-line"}`}
                                      >
                                        <input
                                          type="text"
                                          inputMode="decimal"
                                          placeholder="0"
                                          value={getInputValue(pkg.id, field)}
                                          onFocus={(e) => e.target.select()}
                                          onChange={(e) => {
                                            const v = e.target.value.replace(
                                              ",",
                                              "."
                                            );
                                            if (/^\d*\.?\d?$/.test(v))
                                              handlePackageFieldChange(
                                                pkg.id,
                                                field,
                                                v
                                              );
                                          }}
                                          disabled={!!pkg.selectedPresetId}
                                          className="flex-1 h-full px-3 text-body font-semibold text-ink bg-transparent outline-none min-w-0 disabled:cursor-not-allowed disabled:text-slate"
                                        />
                                        <Unit className="flex h-full select-none items-center border-l border-line px-3">
                                          cm
                                        </Unit>
                                      </div>
                                      {getInputError(pkg.id, field) && (
                                        <p className="text-micro text-danger">
                                          {getInputError(pkg.id, field)}
                                        </p>
                                      )}
                                    </div>
                                  ))}
                                </div>

                                {/* Vertical separator */}
                                <div className="w-px bg-line self-stretch shrink-0" />

                                {/* Peso + Valor column */}
                                {(() => {
                                  const item = (pkg.items || [])[0];
                                  if (!item) return null;
                                  const weightRaw =
                                    getItemInputValue(
                                      pkg.id,
                                      item.id,
                                      "weightValue"
                                    ) ??
                                    (item.weightValue != null
                                      ? String(item.weightValue)
                                      : "");
                                  const weightError = getItemInputError(
                                    pkg.id,
                                    item.id,
                                    "weightVolume"
                                  );
                                  const valueError = getItemInputError(
                                    pkg.id,
                                    item.id,
                                    "unitValueBRL"
                                  );
                                  return (
                                    <div className="flex flex-col gap-3 flex-1 min-w-0">
                                      <div className="relative group flex flex-col gap-1">
                                        <FieldLabel
                                          className={
                                            weightError ? "!text-danger" : ""
                                          }
                                        >
                                          Peso
                                        </FieldLabel>
                                        {!!pkg.selectedPresetId && (
                                          <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 whitespace-nowrap rounded-chip bg-ink px-2.5 py-1 text-micro font-semibold text-paper opacity-0 group-hover:opacity-100 transition-opacity z-20">
                                            Modelos salvos não podem ser
                                            editados
                                          </span>
                                        )}
                                        <div
                                          className={`flex items-center h-12 rounded-control border bg-paper overflow-hidden focus-within-secondary transition-colors ${weightError ? "border-danger" : "border-line"}`}
                                        >
                                          <input
                                            type="text"
                                            inputMode="decimal"
                                            placeholder="0"
                                            value={weightRaw}
                                            onFocus={(e) => e.target.select()}
                                            onChange={(e) => {
                                              const v = e.target.value.replace(
                                                ",",
                                                "."
                                              );
                                              if (/^\d*\.?\d{0,2}$/.test(v))
                                                handleItemFieldChange(
                                                  pkg.id,
                                                  item.id,
                                                  "weightValue",
                                                  v
                                                );
                                            }}
                                            disabled={!!pkg.selectedPresetId}
                                            className="flex-1 h-full px-3 text-body font-semibold text-ink bg-transparent outline-none min-w-0 disabled:cursor-not-allowed disabled:text-slate"
                                          />
                                          <Unit className="flex h-full select-none items-center border-l border-line px-3">
                                            kg
                                          </Unit>
                                        </div>
                                        {weightError && (
                                          <p className="text-micro text-danger">
                                            {weightError}
                                          </p>
                                        )}
                                      </div>
                                      <div className="relative group flex flex-col gap-1">
                                        <FieldLabel
                                          className={
                                            valueError ? "!text-danger" : ""
                                          }
                                        >
                                          Valor declarado
                                        </FieldLabel>
                                        {!!pkg.selectedPresetId && (
                                          <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 whitespace-nowrap rounded-chip bg-ink px-2.5 py-1 text-micro font-semibold text-paper opacity-0 group-hover:opacity-100 transition-opacity z-20">
                                            Modelos salvos não podem ser
                                            editados
                                          </span>
                                        )}
                                        <div
                                          className={`flex items-center h-12 rounded-control border bg-paper overflow-hidden focus-within-secondary transition-colors ${valueError ? "border-danger" : "border-line"}`}
                                        >
                                          <Unit className="flex h-full select-none items-center border-r border-line px-3">
                                            R$
                                          </Unit>
                                          <input
                                            type="text"
                                            inputMode="numeric"
                                            placeholder="0,00"
                                            value={
                                              valorDisplay[item.id] ??
                                              (item.unitValueBRL
                                                ? item.unitValueBRL.toLocaleString(
                                                    "pt-BR",
                                                    {
                                                      minimumFractionDigits: 2,
                                                      maximumFractionDigits: 2,
                                                    }
                                                  )
                                                : "")
                                            }
                                            onFocus={(e) => e.target.select()}
                                            onChange={(e) => {
                                              const digits =
                                                e.target.value.replace(
                                                  /\D/g,
                                                  ""
                                                );
                                              const cents = parseInt(
                                                digits || "0",
                                                10
                                              );
                                              const display = (
                                                cents / 100
                                              ).toLocaleString("pt-BR", {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                              });
                                              setValorDisplay((p) => ({
                                                ...p,
                                                [item.id]: display,
                                              }));
                                              handleItemFieldChange(
                                                pkg.id,
                                                item.id,
                                                "unitValueBRL",
                                                String(cents / 100)
                                              );
                                            }}
                                            disabled={!!pkg.selectedPresetId}
                                            className="flex-1 h-full px-3 text-body font-semibold text-ink bg-transparent outline-none min-w-0 disabled:cursor-not-allowed disabled:text-slate"
                                          />
                                        </div>
                                        {valueError && (
                                          <p className="text-micro text-danger">
                                            {valueError}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Add package button — full width, bottom of list */}
              <button
                id="btn-encomendas-add-package"
                type="button"
                onClick={addPackage}
                className="w-full h-14 bg-paper rounded-card border border-dashed border-mist text-body font-bold text-slate hover:border-ink hover:text-ink transition-colors flex items-center justify-center gap-3"
              >
                <span className="w-7 h-7 rounded-full border-2 border-current flex items-center justify-center text-lede leading-none">
                  +
                </span>
                Novo pacote
              </button>
            </section>

            {/* Quote panel — right side. Sticky on desktop; flows inline on
                mobile. With more than one package the card detaches and floats
                at the bottom-right of the viewport, following the scroll until
                the end of the content, where `quotePanelBottom` parks it just
                above the footer. The column keeps its width either way, so the
                package list never reflows. */}
            <div
              ref={quoteColumnRef}
              className="w-full lg:w-[360px] shrink-0 self-start lg:sticky lg:top-24"
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={quotePanelFloating ? "floating" : "inline"}
                  ref={setQuoteCardEl}
                  className={
                    quotePanelFloating
                      ? "card lg:fixed lg:z-40 lg:w-[360px] lg:shadow-float"
                      : "card"
                  }
                  style={quotePanelFloating ? quotePanelInset : undefined}
                  initial={{ opacity: 0, y: 16, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.98 }}
                  transition={{ duration: 0.22, ease: "easeOut" }}
                >
                  <SectionHeader
                    title="Cotação instantânea"
                    // `quotePanelFloating` já é `packages.length > 1`, então o
                    // plural nunca cai em "1 pacotes" e a linha só aparece no
                    // card flutuante — que é justamente o caso de vários.
                    description={
                      quotePanelFloating
                        ? `${packages.length} pacotes`
                        : undefined
                    }
                    icon={
                      <Truck
                        className="w-5 h-5"
                        style={{ color: primaryColor }}
                      />
                    }
                  />

                  <div className="px-6 py-5">
                    <SubHeader className="mb-4">Opção de envio</SubHeader>
                    {cotacaoLoading && cotacaoOptions.length === 0 ? (
                      // First load — no options yet: show card shell with spinner
                      <div className="flex flex-col gap-4">
                        <div className="rounded-control border border-line bg-paper px-4 py-3">
                          <p className="text-fine font-bold text-ink">Envio</p>
                          <p className="text-micro text-slate mt-0.5">
                            Calculando…
                          </p>
                          <div className="flex items-center gap-2 mt-1 h-7">
                            <Loader2
                              className="w-4 h-4 animate-spin shrink-0"
                              style={{ color: primaryColor }}
                            />
                          </div>
                        </div>
                        <button
                          id="btn-encomendas-cotacao-loading"
                          type="button"
                          disabled
                          className="w-full h-11 rounded-control text-fine font-bold text-paper opacity-40 cursor-not-allowed flex items-center justify-center gap-2"
                          style={{ backgroundColor: primaryColor }}
                        >
                          <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                          Carregando cotação
                        </button>
                      </div>
                    ) : cotacaoOptions.length > 0 ? (
                      <div className="flex flex-col gap-4">
                        {cotacaoOptions.map((opt) => (
                          <div
                            key={opt.id}
                            className="rounded-control border border-line bg-paper px-4 py-3"
                          >
                            <p className="text-fine font-bold text-ink">
                              {opt.label}
                            </p>
                            {opt.etaBusinessDays > 0 && (
                              <p className="text-micro text-slate mt-0.5">
                                {opt.etaBusinessDays === 1
                                  ? "1 dia útil"
                                  : `${opt.etaBusinessDays} dias úteis`}
                              </p>
                            )}
                            {/* Price row with animated number + inline loading indicator */}
                            <div className="relative inline-flex items-center gap-2 mt-1 h-7 overflow-visible">
                              <div
                                className="overflow-hidden h-7 transition-opacity duration-300"
                                style={{ opacity: cotacaoLoading ? 0.35 : 1 }}
                              >
                                <AnimatePresence
                                  mode="popLayout"
                                  initial={false}
                                  custom={priceAnimDir}
                                >
                                  <motion.p
                                    key={
                                      isCotacaoBlocked
                                        ? "blocked"
                                        : String(opt.priceBRL)
                                    }
                                    custom={priceAnimDir}
                                    className={`text-head font-bold ${
                                      isCotacaoBlocked
                                        ? "text-mist"
                                        : "text-ink"
                                    }`}
                                    variants={{
                                      initial: (dir: number) => ({
                                        y: dir * 20,
                                        opacity: 0,
                                      }),
                                      animate: { y: 0, opacity: 1 },
                                      exit: (dir: number) => ({
                                        y: dir * -20,
                                        opacity: 0,
                                      }),
                                    }}
                                    initial="initial"
                                    animate="animate"
                                    exit="exit"
                                    transition={{
                                      type: "spring",
                                      damping: 28,
                                      stiffness: 360,
                                      mass: 0.85,
                                    }}
                                  >
                                    {/*
                                      Com o formulário incompleto o preço em
                                      tela é de uma cotação que já não vale.
                                      Mostrar o valor antigo engana mais do que
                                      ajuda, então ele vira um traço até os
                                      campos voltarem a fechar.
                                    */}
                                    {isCotacaoBlocked
                                      ? "R$ --"
                                      : opt.priceBRL.toLocaleString("pt-BR", {
                                          style: "currency",
                                          currency: "BRL",
                                        })}
                                  </motion.p>
                                </AnimatePresence>
                              </div>
                              {cotacaoLoading && (
                                <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0 text-slate" />
                              )}
                              {/* Floating delta badge */}
                              <AnimatePresence>
                                {priceDelta && !cotacaoLoading && !isCotacaoBlocked && (
                                  <motion.span
                                    key={priceDelta.key}
                                    className={`absolute left-full ml-2 whitespace-nowrap text-fine font-bold pointer-events-none ${
                                      priceDelta.value > 0
                                        ? "text-danger"
                                        : "text-success"
                                    }`}
                                    initial={{ opacity: 1, y: 0 }}
                                    animate={{
                                      opacity: 0,
                                      y: priceDelta.value > 0 ? -24 : 24,
                                    }}
                                    transition={{
                                      duration: 1.4,
                                      ease: "easeOut",
                                    }}
                                    onAnimationComplete={() =>
                                      setPriceDelta(null)
                                    }
                                  >
                                    {priceDelta.value > 0 ? "+" : ""}
                                    {priceDelta.value.toLocaleString("pt-BR", {
                                      style: "currency",
                                      currency: "BRL",
                                    })}
                                  </motion.span>
                                )}
                              </AnimatePresence>
                            </div>
                          </div>
                        ))}
                        <div className="relative group">
                          {/* Experimental: wide bubblewrap chip in the cotação
                            instantânea Continuar slot. */}
                          <ChipButtonWide
                            id="btn-encomendas-open-cotacao"
                            onClick={
                              quoteRetryExhausted
                                ? retryQuote
                                : () => setCotacaoOpen(true)
                            }
                            active={cotacaoOpen}
                            disabled={
                              !quoteRetryExhausted &&
                              (cotacaoLoading || isCotacaoBlocked || isDirty)
                            }
                            icon={
                              !quoteRetryExhausted && isQuoting ? (
                                <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                              ) : undefined
                            }
                            text={
                              quoteRetryExhausted
                                ? "Tentar novamente"
                                : isQuoting
                                  ? "Carregando cotação"
                                  : "Continuar"
                            }
                          />
                          {isCotacaoBlocked && (
                            <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-[200px] rounded-chip bg-ink px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50">
                              <ul className="flex flex-col gap-0.5">
                                {cotacaoBlockReasons.map((r) => (
                                  <li key={r} className="text-fine text-paper">
                                    · {r}
                                  </li>
                                ))}
                              </ul>
                              <div className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-ink" />
                            </div>
                          )}
                        </div>
                        <AnimatePresence initial={false}>
                          {isCotacaoBlocked && (
                            <motion.div
                              key="cotacao-block-warning"
                              // A altura anima de 0 até o conteúdo, e é isso
                              // que faz o card da cotação crescer e encolher
                              // junto em vez de saltar. O marginTop negativo
                              // cancela exatamente o `gap-4` da coluna: sem
                              // ele, sobrariam 16px de vazio enquanto o aviso
                              // sai de cena.
                              initial={{ opacity: 0, height: 0, marginTop: -16 }}
                              animate={{ opacity: 1, height: "auto", marginTop: 0 }}
                              exit={{ opacity: 0, height: 0, marginTop: -16 }}
                              transition={{
                                duration: 0.32,
                                ease: [0.16, 1, 0.3, 1],
                              }}
                              className="overflow-hidden"
                            >
                              <div
                                role="status"
                                className="rounded-control border border-warning/20 bg-warning-soft/50 px-4 py-3"
                              >
                                <div className="flex items-center gap-2">
                                  <AlertTriangle className="h-4 w-4 shrink-0 text-warning" />
                                  <p className="text-fine font-bold text-ink">
                                    Preencha os valores para continuar
                                  </p>
                                </div>
                                <ul className="mt-2 flex flex-col gap-2">
                                  {cotacaoBlockGroups.map((group) => (
                                    <li key={group.id}>
                                      <p className="text-micro font-bold text-ink">
                                        {group.location}
                                      </p>
                                      <ul className="mt-0.5 flex flex-col gap-0.5">
                                        {group.issues.map((issue) => (
                                          <li
                                            key={issue}
                                            className="text-micro text-slate"
                                          >
                                            · {issue}
                                          </li>
                                        ))}
                                      </ul>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2 py-6 text-center">
                        <p className="text-fine font-semibold text-slate">
                          {isFormReady
                            ? "Calculando opções de envio..."
                            : "Preencha os dados do pacote e a rota para ver as opções de envio."}
                        </p>
                      </div>
                    )}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
          {/* end flex row */}
        </div>
      </div>

      {selectedOrigin && selectedDestination && (
        <CotacaoDialog
          open={cotacaoOpen}
          onClose={() => setCotacaoOpen(false)}
          origin={{
            id: selectedOrigin.id,
            name: selectedOrigin.name,
            ibge_id: selectedOrigin.ibge_id,
            city_name: selectedOrigin.city_name ?? selectedOrigin.name,
            state_uf: selectedOrigin.state_uf ?? selectedOrigin.state ?? "",
          }}
          destination={{
            id: selectedDestination.id,
            name: selectedDestination.name,
            ibge_id: selectedDestination.ibge_id,
            city_name:
              selectedDestination.city_name ?? selectedDestination.name,
            state_uf:
              selectedDestination.state_uf ?? selectedDestination.state ?? "",
          }}
          packages={cotacaoPackages.map((cp) => ({
            ...cp,
            name: packages.find((p) => p.id === cp.id)?.name,
          }))}
          shippingOptions={cotacaoOptions}
          loading={cotacaoLoading}
          error={cotacaoError}
          onRetry={() => {
            setCotacaoError("");
            setCotacaoOptions([]);
            fetchQuickQuote({
              originIbge: selectedOrigin.ibge_id!,
              destinationIbge: selectedDestination.ibge_id!,
              packages: cotacaoPackages,
            })
              .then(setCotacaoOptions)
              .catch(() =>
                setCotacaoError("Falha ao buscar cotações. Tente novamente.")
              );
          }}
          onContinuar={async () => {
            const opts = await fetchQuotes({
              originIbge: selectedOrigin.ibge_id!,
              destinationIbge: selectedDestination.ibge_id!,
              packages: cotacaoPackages,
            });
            setCotacaoOptions(opts);
            const selected = opts[0];
            if (selected) {
              setShippingOption({
                id: selected.id,
                label: selected.label ?? "",
                etaBusinessDays: selected.etaBusinessDays,
                priceBRL: selected.priceBRL,
                quoteId: selected.quoteId,
              });
            }
          }}
        />
      )}
    </div>
  );
}
